import { useCallback, useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Shuffle } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { fireBurst } from "@/components/site/ConfettiLayer";
import { ACCENTS } from "@/data/site";

const KEYS = ["pink", "blue", "yellow", "green", "purple"];
const SIZES = [56, 64, 72, 80, 88, 96];
const rand = (min, max) => min + Math.random() * (max - min);

// Pixel-grid silhouettes: [col, row] cells.
const SHAPES = {
  dinozaur: [
    [0, 0], [0, 1], [0, 2], [1, 2], [2, 2], [3, 2], [4, 2], [5, 2], [6, 2], [6, 1], [1, 3], [4, 3],
  ],
  usmiech: [
    [2, 0], [3, 0], [4, 0],
    [1, 1], [5, 1],
    [0, 2], [6, 2],
    [0, 3], [6, 3],
    [0, 4], [6, 4],
    [1, 5], [5, 5],
    [2, 6], [3, 6], [4, 6],
    [2, 2], [4, 2],
    [2, 4], [3, 5], [4, 4],
  ],
  zamek: [
    [0, 0], [2, 0], [4, 0],
    [0, 1], [1, 1], [2, 1], [3, 1], [4, 1],
    [0, 2], [1, 2], [2, 2], [3, 2], [4, 2],
    [0, 3], [1, 3], [2, 3], [3, 3], [4, 3],
  ],
};

const makeFree = (w, h, n = 12) =>
  Array.from({ length: n }).map((_, i) => {
    const size = SIZES[Math.floor(rand(0, SIZES.length))];
    return {
      id: i,
      key: KEYS[i % KEYS.length],
      size,
      x: rand(0, Math.max(0, w - size)),
      y: rand(0, Math.max(0, h - size)),
    };
  });

const makeShape = (cells, w, h) => {
  const cols = Math.max(...cells.map((c) => c[0])) + 1;
  const rows = Math.max(...cells.map((c) => c[1])) + 1;
  const cell = Math.max(40, Math.min(88, Math.min((w - 40) / cols, (h - 40) / rows)));
  const offX = (w - cols * cell) / 2;
  const offY = (h - rows * cell) / 2;
  return cells.map(([cx, cy], i) => ({
    id: i,
    key: KEYS[i % KEYS.length],
    size: cell,
    x: offX + cx * cell,
    y: offY + cy * cell,
  }));
};

const MENU = [
  { id: "dinozaur", label: "Dinozaur" },
  { id: "usmiech", label: "Uśmiech" },
  { id: "zamek", label: "Zamek" },
];

export const Playground = () => {
  const reduce = useReducedMotion();
  const fieldRef = useRef(null);
  const [caps, setCaps] = useState([]);
  const capsRef = useRef([]);
  const [dragId, setDragId] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  const [mode, setMode] = useState("free");
  const drag = useRef({ id: null, offX: 0, offY: 0, moved: false });

  const sync = useCallback((next) => {
    capsRef.current = next;
    setCaps(next);
  }, []);

  const build = useCallback(
    (m) => {
      const el = fieldRef.current;
      if (!el) return;
      const { width, height } = el.getBoundingClientRect();
      setMode(m);
      sync(m === "free" ? makeFree(width, height) : makeShape(SHAPES[m], width, height));
    },
    [sync]
  );

  useEffect(() => {
    build("free");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fieldRect = () => fieldRef.current?.getBoundingClientRect();

  const onPointerDown = (e, id) => {
    e.preventDefault();
    const rect = fieldRect();
    const cap = capsRef.current.find((c) => c.id === id);
    if (!rect || !cap) return;
    drag.current = {
      id,
      offX: e.clientX - rect.left - cap.x,
      offY: e.clientY - rect.top - cap.y,
      moved: false,
    };
    setDragId(id);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  };

  const onPointerMove = (e) => {
    const rect = fieldRect();
    const d = drag.current;
    if (!rect || d.id == null) return;
    const cur = capsRef.current;
    const cap = cur.find((c) => c.id === d.id);
    if (!cap) return;
    let nx = e.clientX - rect.left - d.offX;
    let ny = e.clientY - rect.top - d.offY;
    nx = Math.max(0, Math.min(nx, rect.width - cap.size));
    ny = Math.max(0, Math.min(ny, rect.height - cap.size));
    if (Math.abs(nx - cap.x) + Math.abs(ny - cap.y) > 3) d.moved = true;
    sync(cur.map((c) => (c.id === d.id ? { ...c, x: nx, y: ny } : c)));
  };

  const onPointerUp = (e) => {
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    const d = drag.current;
    const cur = capsRef.current;
    const cap = cur.find((c) => c.id === d.id);
    setDragId(null);
    drag.current = { id: null, offX: 0, offY: 0, moved: false };
    if (!cap) return;
    if (!d.moved) {
      if (!reduce) fireBurst(e.clientX, e.clientY, ACCENTS[cap.key]);
      return;
    }
    // gently nudge to touch edge-to-edge with the nearest cap within ~22px
    const r1 = cap.size / 2;
    const c1x = cap.x + r1;
    const c1y = cap.y + r1;
    let best = null;
    for (const o of cur) {
      if (o.id === cap.id) continue;
      const r2 = o.size / 2;
      const dx = o.x + r2 - c1x;
      const dy = o.y + r2 - c1y;
      const dist = Math.hypot(dx, dy);
      const gap = dist - (r1 + r2);
      if (gap > 0 && gap <= 22 && (!best || gap < best.gap)) {
        best = { o, dx, dy, dist, gap, minDist: r1 + r2 };
      }
    }
    if (best) {
      const ux = best.dx / best.dist;
      const uy = best.dy / best.dist;
      const rect = fieldRect();
      const nx = Math.max(0, Math.min(best.o.x + best.o.size / 2 - ux * best.minDist - r1, rect.width - cap.size));
      const ny = Math.max(0, Math.min(best.o.y + best.o.size / 2 - uy * best.minDist - r1, rect.height - cap.size));
      sync(cur.map((c) => (c.id === cap.id ? { ...c, x: nx, y: ny } : c)));
    }
  };

  return (
    <section id="playground" data-testid="section-playground" className="border-t border-hair py-12 md:py-20">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <Reveal>
            <span className="eyebrow">Interaktywna strefa</span>
            <h2 className="mt-4 font-heading text-4xl font-bold leading-[1.02] tracking-tight text-ink md:text-6xl">
              Zbuduj to sam
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-graphite md:text-lg">
              Przeciągnij i stykaj nakrętki na ekranie albo wybierz gotowy kształt. Zobacz, jak prosta
              zabawa wciąga na nowo.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap items-center gap-2">
              {MENU.map((m) => (
                <button
                  key={m.id}
                  data-testid={`playground-shape-${m.id}`}
                  onClick={() => build(m.id)}
                  className="border px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] transition-colors"
                  style={{
                    backgroundColor: mode === m.id ? "#F59BBB" : "transparent",
                    color: "#0A0A0A",
                    borderColor: "#0A0A0A",
                  }}
                >
                  {m.label}
                </button>
              ))}
              <button
                data-testid="playground-shape-free"
                onClick={() => build("free")}
                className="flex items-center gap-2 border border-ink px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-ink transition-colors hover:bg-owo-pink"
              >
                <Shuffle size={14} /> Rozsyp
              </button>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div
            ref={fieldRef}
            data-testid="playground-field"
            className="owo-grid-bg relative mt-12 h-[420px] w-full touch-none select-none overflow-hidden border border-hair bg-white md:h-[520px]"
          >
            {caps.map((cap, index) => {
              const isDrag = dragId === cap.id;
              const isHover = hoverId === cap.id;
              return (
                <motion.div
                  key={cap.id}
                  className="absolute"
                  style={{ width: cap.size, height: cap.size }}
                  animate={{ left: cap.x, top: cap.y }}
                  transition={
                    isDrag
                      ? { duration: 0 }
                      : { type: "spring", stiffness: 400, damping: 32, delay: dragId ? 0 : index * 0.03 }
                  }
                >
                  <motion.button
                    data-cap
                    data-testid={`playground-cap-${cap.id}`}
                    data-cursor-color={ACCENTS[cap.key]}
                    onPointerDown={(e) => onPointerDown(e, cap.id)}
                    onMouseEnter={() => setHoverId(cap.id)}
                    onMouseLeave={() => setHoverId((h) => (h === cap.id ? null : h))}
                    aria-label="Nakrętka OWOCAPS"
                    className="relative flex h-full w-full items-center justify-center rounded-full"
                    style={{ backgroundColor: ACCENTS[cap.key], cursor: isDrag ? "grabbing" : "grab" }}
                    animate={{
                      scale: isDrag ? 1.15 : isHover ? 1.1 : 1,
                      y: reduce || isDrag || mode !== "free" ? 0 : [0, -5, 0],
                    }}
                    transition={{
                      scale: { type: "spring", stiffness: 300, damping: 20 },
                      y:
                        reduce || mode !== "free"
                          ? {}
                          : { duration: 3 + (cap.id % 4) * 0.4, repeat: Infinity, ease: "easeInOut", delay: cap.id * 0.2 },
                    }}
                  >
                    <span
                      className="pointer-events-none select-none font-heading font-black uppercase leading-none text-ink"
                      style={{ fontSize: Math.round(cap.size * 0.16), letterSpacing: "-0.02em" }}
                    >
                      OWOCAPS
                    </span>
                  </motion.button>
                </motion.div>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
